// 画像圧縮用のユーティリティ関数

/**
 * Base64データから画像形式を判定
 * @param base64Data - Base64エンコードされた画像データ
 * @returns 'jpeg' | 'png' | 'gif' | 'webp' | 'unknown'
 */
export const detectImageFormat = (base64Data: string): string => {
  // Base64の先頭文字で判定
  if (base64Data.startsWith('/9j/') || base64Data.startsWith('/9i/')) {
    return 'jpeg';
  } else if (base64Data.startsWith('iVBOR')) {
    return 'png';
  } else if (base64Data.startsWith('R0lGOD')) {
    return 'gif';
  } else if (base64Data.startsWith('UklGR')) {
    return 'webp';
  }
  return 'jpeg'; // デフォルトはJPEG（Imagen APIの標準形式）
};

/**
 * Base64データに適切なData URLプレフィックスを付与
 * @param base64Data - Base64エンコードされた画像データ
 * @returns 完全なData URL
 */
export const createDataUrl = (base64Data: string): string => {
  const format = detectImageFormat(base64Data);
  return `data:image/${format};base64,${base64Data}`;
};

/**
 * 画像を圧縮してBase64文字列を返す
 * @param base64Data - 圧縮する画像のBase64データ（data:image/...;base64, の後の部分）
 * @param maxSizeKB - 最大ファイルサイズ（KB）
 * @param inputFormat - 入力画像の形式（png, jpeg等）
 * @returns 圧縮されたBase64文字列（data:image/...;base64, の後の部分のみ）
 */
export const compressImage = async (
  base64Data: string, 
  maxSizeKB: number = 100, 
  inputFormat: string = 'png'
): Promise<string> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // ピクセルアートに適したサイズに調整
      const maxDimension = 512; // 最大512px
      let { width, height } = img;

      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width *= ratio;
        height *= ratio;
      }

      canvas.width = width;
      canvas.height = height;

      // ピクセルアートのため、スムージングを無効化
      if (ctx) {
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, 0, 0, width, height);

        // JPEG品質を調整してファイルサイズを制御
        let quality = 0.9;
        let compressedData = canvas.toDataURL('image/jpeg', quality);

        // ファイルサイズが大きすぎる場合は品質を下げる
        while (compressedData.length > maxSizeKB * 1024 * 4/3 && quality > 0.3) {
          quality -= 0.1;
          compressedData = canvas.toDataURL('image/jpeg', quality);
        }

        resolve(compressedData.split(',')[1]); // Base64部分のみ返す
      } else {
        resolve(base64Data); // フォールバック
      }
    };


    // inputFormatを使用（9/23以前の動作に戻す）
    img.src = `data:image/${inputFormat};base64,${base64Data}`;
  });
};

/**
 * ピクセルアート用の圧縮（100KB制限）
 */
export const compressPixelArt = async (
  base64Data: string, 
  inputFormat: string = 'png'
): Promise<string> => {
  return compressImage(base64Data, 100, inputFormat);
};

/**
 * 元画像用の圧縮（200KB制限）
 */
export const compressOriginalImage = async (
  base64Data: string, 
  inputFormat: string = 'jpeg'
): Promise<string> => {
  return compressImage(base64Data, 200, inputFormat);
};

/**
 * 元画像用の圧縮（300KB制限、Firestore 1MB制限対応）
 * 総データサイズを1MB以下に保つための圧縮
 */
export const compressOriginalImageSafe = async (
  base64Data: string, 
  inputFormat: string = 'jpeg'
): Promise<string> => {
  return compressImage(base64Data, 300, inputFormat);
};
